import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Button } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import StudentSelectionModal from 'components/Modal/StudentSelectionModal';
import AcademyStudentTable from 'components/organisms/Table/AcademyStudentTable';
import { Dialog } from 'components/organisms/Modal/Dialog';
import { Icon } from '@/icons';
import { academyPaperApi, academyStudentApi } from '@/services/api';
import { useSessionSlug } from '@/hooks/useSessionSlug';
import Toast from '@/components/atoms/Toast/Toast';

type TicketStatus = 'AVAILABLE' | 'ASSIGNED' | 'USED' | 'EXPIRED' | 'REFUNDED';
type ExamStatus = 'IN_PROGRESS' | 'SUBMITTED' | 'GRADED';
type GradingStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';

interface AssignedStudent {
    ticketId?: number
    saleId?: number
    studentUserId: number
    studentName: string
    studentEmail: string
    assignedAt: string | null
    pending?: boolean
    ticketStatus?: TicketStatus
    examStatus?: ExamStatus
    gradingStatus?: GradingStatus
    examResultId?: number
    usedAt?: string | null
    examCompletedAt?: string | null
    studentLoginId?: string | null
    warningSubmitted?: boolean
}

export default function AcademyMypagePaperDetail() {
    const { paperId } = useParams<{ paperId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const saleIdParam = searchParams.get('saleId');
    const saleId = saleIdParam ? parseInt(saleIdParam, 10) : null;
    const { academyCode: sessionAcademyCode, status: sessionStatus } = useSessionSlug();
    const academyCode = sessionAcademyCode || '';
    
    const [showStudentModal, setShowStudentModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [refundCount, setRefundCount] = useState(1);
    const [refundError, setRefundError] = useState<string | null>(null); // [QA Fix] 환불 수량 한도 안내
    const [loading, setLoading] = useState(false);
    const [refunding, setRefunding] = useState(false);
    const [refundResult, setRefundResult] = useState<{ refundedCount: number; refundAmount: number } | null>(null);
    const [toast, setToast] = useState<{ message: string; status: 'success' | 'error' | 'info' } | null>(null);
    const [revokeTarget, setRevokeTarget] = useState<AssignedStudent | null>(null);
    const [revoking, setRevoking] = useState(false);
    
    // 시험지 정보
    const [paper, setPaper] = useState<any>(null);
    const [ticketStats, setTicketStats] = useState<any>(null);
    const [saleStats, setSaleStats] = useState<any>(null);
    
    // 배정된 학생 목록(서버 확정)
    const [assignedStudents, setAssignedStudents] = useState<AssignedStudent[]>([]);
    
    // 학원 전체 학생 목록 (모달용)
    const [availableStudents, setAvailableStudents] = useState<any[]>([]);
    const [loadingStudents, setLoadingStudents] = useState(false);
    
    // 데이터 로드
    useEffect(() => {
        if (!paperId) return;
        loadPaperDetail();
    }, [paperId, saleId]);

    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(null), 3000);
        return () => clearTimeout(timer);
    }, [toast]);

    const showToast = useCallback((message: string, status: 'success' | 'error' | 'info' = 'info') => {
        setToast({ message, status });
    }, []);
    
    const loadPaperDetail = async () => {
        if (!paperId) return;
        
        try {
            setLoading(true);
            
            // 시험지 상세 정보 조회
            const paperRes: any = await academyPaperApi.detail(parseInt(paperId));
            const paperData = paperRes?.data || paperRes;
            setPaper(paperData);
            
            // 응시권 통계 조회 (JWT에서 자동으로 academyId 추출)
            const statsRes: any = await academyPaperApi.getTicketStats(parseInt(paperId));
            const statsData = statsRes?.data || statsRes;
            setTicketStats(statsData);

            let currentSaleStats = statsData;
            if (saleId) {
                try {
                    const saleStatsRes: any = await academyPaperApi.getTicketStats(parseInt(paperId), { saleId });
                    currentSaleStats = saleStatsRes?.data || saleStatsRes;
                } catch (error) {
                    console.error('구매 이력 통계 조회 실패:', error);
                }
            }
            setSaleStats(currentSaleStats);
            
            // 배정된 학생 목록 조회
            await loadAssignedStudents();
            
            // 환불 가능한 최대 수량 설정 (미사용 응시권)
            if ((currentSaleStats?.refundableTickets ?? 0) > 0) {
                setRefundCount(1);
                setRefundError(null);
            }
            
        } catch (error) {
            console.error('시험지 정보 조회 실패:', error);
            showToast('시험지 정보를 불러오는데 실패했습니다.', 'error');
            navigate('/academy/mypage/paper');
        } finally {
            setLoading(false);
        }
    };
    
    const loadAssignedStudents = async () => {
        if (!paperId) return;
        
        try {
            const res: any = await academyPaperApi.getAssignedStudents(parseInt(paperId));
            const data = res?.data || res;
            setAssignedStudents(data || []);
        } catch (error) {
            console.error('배정된 학생 목록 조회 실패:', error);
            setAssignedStudents([]);
        }
    };
    
    const loadAvailableStudents = async () => {
        if (!academyCode || sessionStatus !== 'ready') {
            showToast('학원 코드를 찾을 수 없습니다.', 'error');
            return;
        }
        
        try {
            setLoadingStudents(true);
            const res: any = await academyStudentApi.list({
                academyCode,
                page: 0,
                size: 100 // 전체 조회 (실제로는 페이징 처리 필요)
            });
            
            const data = res?.data || res;
            const content = data?.content || data || [];
            
            // 형식 변환
            const students = content.map((s: any) => ({
                id: (s.userId ?? s.academyStudentId ?? s.id ?? s.studentUserId)?.toString(),
                loginId: s.studentLoginId || s.loginId || s.userLoginId || s.userAccount?.loginId || '',
                name: s.name || s.studentName,
                email: s.email || s.userAccount?.email || '',
                grade: s.gradeLabel || '',
                status: s.status ? (s.status === '수강중' ? 'active' : 'inactive') : 'active',
                registrationDate: s.registrationDate || s.createdAt || s.enrolledAt || '',
                academyCode: academyCode,
                birthDate: s.birthDate || ''
            }));
            
            setAvailableStudents(students);
        } catch (error) {
            console.error('학생 목록 조회 실패:', error);
            showToast('학생 목록을 불러오는데 실패했습니다.', 'error');
            setAvailableStudents([]);
        } finally {
            setLoadingStudents(false);
        }
    };
    
    const handleOpenStudentModal = async () => {
        await loadAvailableStudents();
        setShowStudentModal(true);
    };

    const handleStudentModalOk = async (selectedStudents: any[]) => {
        if (selectedStudents.length === 0) {
            showToast('학생을 선택해주세요.', 'info');
            return;
        }
        // 이미 배정된 사용자 제외 (현재 ASSIGNED 상태인 경우에만 중복 차단)
        const alreadyAssignedIds = new Set(
            assignedStudents
                .filter(s => s.ticketStatus === 'ASSIGNED')
                .map(s => s.studentUserId)
        );
        const uniqueIds = selectedStudents
            .map((s: any) => parseInt(s.id))
            .filter((id: number) => !alreadyAssignedIds.has(id));

        if (uniqueIds.length === 0) {
            showToast('이미 배정된 학생입니다.', 'info');
            setShowStudentModal(false);
            return;
        }

        // 응시권 수 체크 (배정을 위해 사용 가능한 수량만 확인)
        if ((ticketStats?.available ?? 0) < uniqueIds.length) {
            showToast(`응시권이 부족합니다. (필요: ${uniqueIds.length}, 가능: ${ticketStats?.available || 0})`, 'error');
            return;
        }

        try {
            const payload: { studentUserIds: number[]; saleId?: number } = {
                studentUserIds: uniqueIds,
            };
            if (saleId) {
                payload.saleId = saleId;
            }
            const res: any = await academyPaperApi.assignStudents(parseInt(paperId!), payload);
            const data = res?.data || res;
            showToast(res?.message || `${data.assignedCount}명의 학생에게 응시권이 배정되었습니다.`, 'success');
            await loadPaperDetail();
            setShowStudentModal(false);
        } catch (error: any) {
            console.error('학생 배정 실패:', error);
            const message = error?.response?.data?.message || error?.message || '학생 배정에 실패했습니다.';
            showToast(message, 'error');
        }
    };

    // 저장/취소 흐름 제거 (퍼블 원복)

    const handleStudentModalCancel = () => {
        setShowStudentModal(false);
    };

    const handleRefundClick = () => {
        setShowRefundModal(true);
    };

    const handleRefundModalOk = async () => {
        if (refunding) return;
        if (!saleId) {
            showToast('환불할 구매 이력이 선택되지 않았습니다.', 'error');
            return;
        }
        
        try {
            setRefunding(true);
            
            // 환불 처리
            const refundPayload: { quantity: number; saleId?: number } = { quantity: refundCount };
            if (saleId) {
                refundPayload.saleId = saleId;
            }
            const res: any = await academyPaperApi.refundTickets(parseInt(paperId!), refundPayload);
            
            const data = res?.data || res;
            
            const normalizedRefundAmount = Number(data?.refundAmount ?? 0);
            setRefundResult({
                refundedCount: data?.refundedCount ?? refundCount,
                refundAmount: Number.isFinite(normalizedRefundAmount) ? normalizedRefundAmount : 0,
            });
            
            // 데이터 새로고침
            await loadPaperDetail();
            
            setShowRefundModal(false);
            setRefundCount(1);
            setRefundError(null);
        } catch (error: any) {
            console.error('환불 처리 실패:', error);
            const fallbackMessage = '환불 처리 중 오류가 발생했습니다. 다시 시도해주세요.'; // [QA Fix] PG 오류 고정 메시지
            const message = error?.response?.data?.message || fallbackMessage;
            showToast(message, 'error');
        } finally {
            setRefunding(false);
        }
    };

    const handleRefundModalCancel = () => {
        setShowRefundModal(false);
        setRefundCount(1);
        setRefundError(null);
    };

    const handleRefundResultClose = () => {
        setRefundResult(null);
    };

    const handleRefundCountChange = (newCount: number) => {
        const maxRefund = saleStats?.refundableTickets ?? 0;
        if (!maxRefund) {
            setRefundCount(1);
            setRefundError(null);
            return;
        }

        const nextValue = Math.max(1, newCount);
        if (nextValue > maxRefund) {
            setRefundCount(maxRefund);
            setRefundError(`최대 ${maxRefund}개까지만 환불 가능합니다.`); // [QA Fix] 한도 초과 시 안내
            return;
        }

        setRefundError(null);
        setRefundCount(nextValue);
    };
    
    const handleRequestRevoke = (student: AssignedStudent) => {
        if (student.warningSubmitted) {
            showToast('경고 누적으로 종료된 응시권은 회수할 수 없습니다.', 'info');
            return;
        }
        if (student.ticketStatus && student.ticketStatus !== 'ASSIGNED') {
            showToast('응시가 시작된 응시권은 회수할 수 없습니다.', 'info');
            return;
        }
        if (!student.ticketId) {
            showToast('아직 저장되지 않은 배정입니다. 저장 후 회수할 수 있습니다.', 'info');
            return;
        }
        setRevokeTarget(student);
    };

    const handleConfirmRevoke = async () => {
        if (!revokeTarget || revoking) return;
        if (!revokeTarget.ticketId) {
            showToast('아직 저장되지 않은 배정입니다. 저장 후 회수할 수 있습니다.', 'info');
            setRevokeTarget(null);
            return;
        }

        try {
            setRevoking(true);
            await academyPaperApi.revokeTicket(parseInt(paperId!), revokeTarget.ticketId);
            showToast('응시권이 회수되었습니다.', 'success');

            await loadPaperDetail();
        } catch (error: any) {
            console.error('응시권 회수 실패:', error);
            const message = error?.response?.data?.message || error?.message || '응시권 회수에 실패했습니다.';
            showToast(message, 'error');
        } finally {
            setRevoking(false);
            setRevokeTarget(null);
        }
    };
    const displayedStats = saleId ? saleStats : ticketStats;
    const displayedAssignedStudents = saleId
        ? assignedStudents.filter((student) => Number(student.saleId) === Number(saleId))
        : assignedStudents;
    const refundingOverlay = refunding ? (
        <div
            className='fixed inset-0 z-[12000] flex flex-col gap-4 justify-center items-center backdrop-blur-[1px]'
            style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
        >
            <div className='flex flex-col gap-4 justify-center items-center p-8 bg-white rounded-2xl shadow-xl'>
                <div className='w-16 h-16 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin' />
                <div className='text-center'>
                    <p className='text-xl font-bold text-gray-900'>환불 처리 중입니다</p>
                    <p className='mt-2 text-sm text-gray-500'>잠시만 기다려주세요...</p>
                </div>
            </div>
        </div>
    ) : null;
    
    if (loading) {
        return (
            <main className='flex flex-col flex-1 py-8'>
                <div className='flex items-center justify-center py-20'>
                    <p className='text-gray-500'>로딩 중...</p>
                </div>
                {refundingOverlay}
            </main>
        );
    }

    if (!paper) {
        return (
            <main className='flex flex-col flex-1 py-8'>
                <div className='flex items-center justify-center py-20'>
                    <p className='text-gray-500'>시험지 정보를 찾을 수 없습니다.</p>
                </div>
                {refundingOverlay}
            </main>
        );
    }

    return (
        <main className='flex flex-col flex-1 py-8'>
            <div className='flex flex-row justify-between items-center pb-5'>
                <h1 className='heading1'>시험지 관리</h1>
                <Button intent='secondary' onClick={() => navigate('/academy/mypage/paper')}>목록으로</Button>
            </div>
        <ul>
            <li className='flex py-5 border-b border-gray-100'>
                <p className='flex-none max-w-[150px] w-full text-gray-500'>시험명</p>
                <p>{paper?.title || '-'}</p>
            </li>
            
            <li className='flex py-5 border-b border-gray-100'>
                <p className='flex-none max-w-[150px] w-full text-gray-500'>채점 유형</p>
                <p>{paper?.aiScoring ? 'AI 채점' : '일반 채점'}</p>
            </li>
            
            <li className='flex py-5 border-b border-gray-100'>
                <p className='flex-none max-w-[150px] w-full text-gray-500'>문항 수</p>
                <p>{paper?.questionCount || 0}문제</p>
            </li>

            <li className='flex py-5 border-b border-gray-100'>
                <p className='flex-none max-w-[150px] w-full text-gray-500'>응시권 수</p>
                <div className='w-full'>
                    <div className='grid grid-cols-3 gap-2 *:px-5 *:py-3 w-full *:bg-gray-50 *:rounded-xl'>
                        <div className='flex justify-between'>
                            <p>최초 구매 응시권 수</p>
                            <p className='font-bold text-indigo-500'>{displayedStats?.totalPurchased || 0}</p>
                        </div>

                        <div className='flex justify-between'>
                            <p>소진한 응시권 수</p>
                            <p className='font-bold text-indigo-500'>{displayedStats?.used || 0}</p>
                        </div>

                        <div className='flex justify-between'>
                            <p>분배 가능 응시권 수</p>
                            <p className='font-bold text-indigo-500'>{displayedStats?.available || 0}</p>
                        </div>

                        <div className='flex justify-between text-orange-500'>
                            <p>환불 완료 응시권 수</p>
                            <p className='font-bold'>{displayedStats?.refunded || 0}</p>
                        </div>

                        <div className='flex justify-between text-gray-500'>
                            <p>배정된 응시권 수</p>
                            <p className='font-bold'>{displayedStats?.assigned || 0}</p>
                        </div>
                    </div>
                    <p className='block mt-2 text-sm text-gray-500'>
                        {saleId
                            ? '*해당 구매 건 기준으로 표기된 수량입니다.'
                            : '*전체 시험지 기준으로 표기된 수량입니다.'}
                        <br />
                        *환불은 구매 후 7일 이내에만 가능합니다<br />
                        *분배 가능 응시권 수 = 최초 구매 - 소진 - 환불<br />
                        *구매 후 1년이 지나면 자동 소멸됩니다
                    </p>
                </div>
            </li>

            <li className='flex py-5 border-b border-gray-100'>
                <p className='flex-none max-w-[150px] w-full text-gray-500'>학습 대상</p>
                <div className='w-full'>
                    <div className='flex gap-2 items-center w-full max-w-[500px] mb-5'>
                        <Input
                            placeholder='학습 대상을 입력해주세요.'
                            className='w-full'
                        />
                        <Button className='flex-none' onClick={handleOpenStudentModal}>
                            {loadingStudents ? '로딩 중...' : '불러오기'}
                        </Button>
                    </div>
                    <AcademyStudentTable
                        data={displayedAssignedStudents.map((s) => ({
                            id: String(s.studentUserId),
                            loginId: s.studentLoginId || (s as any).loginId || '',
                            name: s.studentName,
                            email: s.studentEmail || '',
                            grade: '',
                            status: 'active',
                            registrationDate: s.assignedAt || '',
                            academyCode: academyCode || '-',
                            birthDate: (s as any).birthDate || '-',
                            examTicketStatus: s.ticketStatus,
                            examGradingStatus: s.gradingStatus,
                            warningSubmitted: s.warningSubmitted
                        }))}
                        selectedStudents={[]}
                        onStudentSelect={() => {}}
                        onSelectAll={() => {}}
                        currentPage={1}
                        pageSize={10}
                        showSearch={false}
                        showPagination={false}
                        type='display'
                        displayType='exam'
                        total={displayedAssignedStudents.length}
            showAcademyCode={false}
                        onExamAction={(studentId) => {
                            const found = displayedAssignedStudents.find(st => String(st.studentUserId) === String(studentId));
                            if (!found) return;
                            handleRequestRevoke(found);
                        }}
                    />
                </div>
            </li>

            <li className='flex py-5 border-b border-gray-100'>
                <p className='flex-none max-w-[150px] w-full text-gray-500'>시험지 가격</p>
                <p>{paper?.price ? `${paper.price.toLocaleString()}원` : '-'}</p>
            </li>
        </ul>
        <div className='flex flex-col gap-2 justify-end mt-5'>
            {saleId ? (
                <div className='flex gap-2 justify-end *:w-full *:max-w-[200px]'>
                    {saleStats?.canRefund && (saleStats?.refundableTickets ?? 0) > 0 && (
                        <Button intent='secondary' onClick={handleRefundClick}>환불하기</Button>
                    )}
                    <Button intent='primary' onClick={() => navigate('/academy/mypage/paper')}>확인</Button>
                </div>
            ) : (
                <div className='flex flex-col gap-2'>
                    <p className='text-sm text-right text-gray-500'>환불은 마이페이지 목록에서 구매 건을 선택해 접근해야 합니다.</p>
                    <div className='flex gap-2 justify-end *:w-full *:max-w-[200px]'>
                        <Button intent='primary' onClick={() => navigate('/academy/mypage/paper')}>확인</Button>
                    </div>
                </div>
            )}
        </div>

        <StudentSelectionModal
            open={showStudentModal}
            onOk={handleStudentModalOk}
            onCancel={handleStudentModalCancel}
            availableStudents={availableStudents}
            showAcademyCode={false}
        />

        <Dialog
            open={!!revokeTarget}
            onOk={handleConfirmRevoke}
            onCancel={() => {
                if (revoking) return;
                setRevokeTarget(null);
            }}
            okText={revoking ? '회수 중...' : '회수하기'}
            cancelText="취소"
            className="max-w-lg"
        >
            <div className='p-6 space-y-4'>
                <h3 className='text-xl font-bold text-gray-900 text-center'>응시권 회수</h3>
                <p className='text-sm text-center text-gray-500'>회수 시 응시권은 다시 분배 가능한 상태가 됩니다.</p>
                <div className='p-4 text-center bg-gray-50 rounded-2xl'>
                    <p className='text-base text-gray-700 whitespace-pre-line'>
                        {revokeTarget
                            ? `${revokeTarget.studentName} 학생의 응시권을 회수하시겠습니까?\n응시 내역이 없는 경우에만 회수가 가능합니다.`
                            : '회수할 응시권 정보를 불러오는 중입니다.'}
                    </p>
                </div>
            </div>
        </Dialog>

        {showRefundModal && (
            <Dialog
                onOk={handleRefundModalOk}
                onCancel={handleRefundModalCancel}
                okText={refunding ? "처리 중..." : "환불하기"}
                cancelText="취소"
            >
                    <div className="p-6">
                        <div className="space-y-5">
                            <div className="p-4 mb-4 bg-gray-50 rounded-lg">
                                <h4 className="text-xl font-bold">{paper?.title} 응시권 환불</h4>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <p className='text-sm font-semibold text-center text-gray-500'>환불 갯수</p>
                                <div className='flex justify-center items-center rounded-xl border border-gray-200 *:border-gray-200 w-full max-w-[156px] mx-auto'>
                                    <button 
                                        className='flex-none border-r size-11'
                                        onClick={() => handleRefundCountChange(refundCount - 1)}
                                        disabled={refunding}
                                    >
                                        -
                                    </button>
                                    <input 
                                        placeholder="1" 
                                        type="number" 
                                        value={refundCount}
                                        onChange={(e) => handleRefundCountChange(parseInt(e.target.value) || 1)}
                                        disabled={refunding}
                                        style={{
                                            appearance: 'textfield',
                                        }}
                                        className='w-full text-lg text-center border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' 
                                    />
                                    <button 
                                        className='flex-none border-l size-11'
                                        onClick={() => handleRefundCountChange(refundCount + 1)}
                                        disabled={refunding}
                                    >
                                        +
                                    </button>
                                </div>
                                <p className={`mt-1 text-sm text-center ${refundError ? 'text-red-500' : 'text-gray-400'}`}>
                                    {refundError || `최대 ${(saleStats?.refundableTickets || 0)}개까지만 환불 가능합니다.`}
                                </p>
                            </div>
                            <section className='flex flex-col gap-2'>
                                <div className='flex gap-2 justify-between pt-5 text-xl font-bold border-t border-gray-200'>
                                    <h2 className=''>환불금액</h2>
                                    <p className='text-indigo-500'>
                                        {(((saleStats?.unitPrice ?? ticketStats?.unitPrice) || 0) * refundCount).toLocaleString()}원
                                    </p>
                                </div>
                                <p className='text-sm text-gray-400'>
                                    환불은 pg사 영업일 기준 1~5일 소요될 수 있습니다.<br />
                                    구매 후 7일 이내에만 환불이 가능합니다.<br />
                                    배정된 응시권이라도 시험에 응시하지 않았다면 환불 시 자동으로 회수됩니다.
                                </p>
                            </section>
                        </div>
                    </div>
                </Dialog>
        )}
        {refundResult && (
            <Dialog
                onOk={handleRefundResultClose}
                onCancel={handleRefundResultClose}
                okText="확인"
                cancelText="닫기"
            >
                <div className='p-6 text-center'>
                    <h3 className='text-lg font-bold text-gray-900 mb-2'>환불이 완료되었습니다.</h3>
                    <p className='text-sm text-gray-500 mb-4'>환불 결과를 확인해주세요.</p>
                    <div className='flex flex-col gap-3 p-4 bg-gray-50 rounded-xl text-sm text-gray-700'>
                        <div className='flex justify-between'>
                            <span>환불 수량</span>
                            <span className='font-semibold text-indigo-600'>{refundResult.refundedCount}개</span>
                        </div>
                        <div className='flex justify-between'>
                            <span>환불 금액</span>
                            <span className='font-semibold text-indigo-600'>{refundResult.refundAmount.toLocaleString()}원</span>
                        </div>
                    </div>
                    <p className='mt-4 text-xs text-gray-400'>환불 금액은 PG사 영업일 기준 1~5일 내 입금됩니다.</p>
                </div>
            </Dialog>
        )}

        {toast && (
            <Toast
                position='top-center'
                status={toast.status}
                message={toast.message}
                isVisible={!!toast}
            />
        )}
        {refundingOverlay}
    </main>
  )
}